/**
 * Hook pour la gestion des feedbacks
 *
 * Responsabilités:
 * - Gestion des états de requête (loading, error, success)
 * - Appel de l'API feedback
 * - PAS de logique UI
 */

import { useState, useCallback } from 'react';
import { submitFeedback, FeedbackResponse } from './feedback.api';

export type FeedbackStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseFeedbackResult {
  /** État actuel de la requête */
  status: FeedbackStatus;
  /** Indique si une requête est en cours */
  isLoading: boolean;
  /** Indique si le dernier envoi a réussi */
  isSuccess: boolean;
  /** Indique si le dernier envoi a échoué */
  isError: boolean;
  /** Message d'erreur (si erreur) */
  errorMessage: string | null;
  /** Code d'erreur (si erreur) */
  errorCode: string | null;
  /** Délai avant retry (si rate limit) */
  retryAfter: number | null;
  /** ID du feedback créé (si succès) */
  feedbackId: number | null;
  /** Soumet un feedback */
  submit: (content: string) => Promise<boolean>;
  /** Réinitialise l'état */
  reset: () => void;
}

/**
 * Hook pour soumettre des feedbacks
 * @returns État et fonction de soumission
 */
export function useFeedback(): UseFeedbackResult {
  const [status, setStatus] = useState<FeedbackStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [feedbackId, setFeedbackId] = useState<number | null>(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setErrorMessage(null);
    setErrorCode(null);
    setRetryAfter(null);
    setFeedbackId(null);
  }, []);

  const submit = useCallback(async (content: string): Promise<boolean> => {
    // Validation côté client
    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setStatus('error');
      setErrorMessage('Le message ne peut pas être vide');
      setErrorCode('EMPTY_CONTENT');
      return false;
    }

    if (trimmedContent.length > 2000) {
      setStatus('error');
      setErrorMessage('Le message est trop long (max 2000 caractères)');
      setErrorCode('CONTENT_TOO_LONG');
      return false;
    }

    // Récupérer la route actuelle
    const route = window.location.pathname;

    setStatus('loading');
    setErrorMessage(null);
    setErrorCode(null);
    setRetryAfter(null);

    try {
      const response: FeedbackResponse = await submitFeedback({
        content: trimmedContent,
        route,
      });

      if (response.success) {
        setStatus('success');
        setFeedbackId(response.feedback?.id ?? null);
        return true;
      } else {
        setStatus('error');
        setErrorCode(response.error_code ?? 'UNKNOWN_ERROR');

        // Messages d'erreur utilisateur selon le code
        switch (response.error_code) {
          case 'AUTH_REQUIRED':
          case 'SESSION_INVALID':
            setErrorMessage('Veuillez vous connecter pour envoyer un retour');
            break;
          case 'RATE_LIMIT_EXCEEDED':
            setErrorMessage(
              response.error ||
                'Vous avez atteint la limite. Réessayez plus tard.'
            );
            setRetryAfter(response.retryAfter ?? null);
            break;
          case 'NETWORK_ERROR':
            setErrorMessage('Impossible de contacter le serveur');
            break;
          default:
            setErrorMessage(response.error || 'Une erreur est survenue');
        }
        return false;
      }
    } catch (error) {
      console.error('[useFeedback] Erreur inattendue:', error);
      setStatus('error');
      setErrorMessage('Une erreur inattendue est survenue');
      setErrorCode('UNEXPECTED_ERROR');
      return false;
    }
  }, []);

  return {
    status,
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    isError: status === 'error',
    errorMessage,
    errorCode,
    retryAfter,
    feedbackId,
    submit,
    reset,
  };
}

export default useFeedback;
